import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  makeStyles,
  Typography,
} from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { ContentPageStyles } from "./styles";

const RACEPACE_HERO_URL =
  "https://resources.lifeinfoapp.com/img/racepace/1.jpg";
const RACEPACE_LOGO_URL =
  "https://resources.lifeinfoapp.com/img/racepace/Starfish-Logo.png";
const RACEPACE_WHAT_IMAGE_URL =
  "https://resources.lifeinfoapp.com/img/racepace/2.jpg";
const RACEPACE_ICON_3_URL =
  "https://resources.lifeinfoapp.com/img/racepace/Icon-3.png";
const RACEPACE_ICON_LIST_URL =
  "https://resources.lifeinfoapp.com/img/racepace/Icon-List.png";
const RACEPACE_ICON_CALENDAR_URL =
  "https://resources.lifeinfoapp.com/img/racepace/Icon-Calendar.png";
const RACEPACE_DRIVERS_PHOTO_1_URL =
  "https://resources.lifeinfoapp.com/img/racepace/Drivers---1.jpg";
const RACEPACE_DRIVERS_PHOTO_2_URL =
  "https://resources.lifeinfoapp.com/img/racepace/Drivers---2.jpg";
const RACEPACE_ICON_COACH_URL =
  "https://resources.lifeinfoapp.com/img/racepace/Icon-Coach.png";
const RACEPACE_ICON_ENCOURAGE_URL =
  "https://resources.lifeinfoapp.com/img/racepace/Icon-Encourage.png";
const RACEPACE_ICON_HELP_OTHERS_URL =
  "https://resources.lifeinfoapp.com/img/racepace/Icon-Help-Opthers.png";

const useStyles = makeStyles((theme) => ({
  ...ContentPageStyles(theme),
  racePaceContainer: {
    position: "relative",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  },
  heroBleed: {
    position: "relative",
    minHeight: "100vh",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  hero: {
    position: "relative",
    height: "100vh",
    minHeight: "100vh",
    overflow: "hidden",
    isolation: "isolate",
    transform: "translateZ(0)",
    backfaceVisibility: "hidden",
  },
  heroMedia: {
    position: "absolute",
    inset: 0,
    zIndex: 0,
    overflow: "hidden",
    transform: "translateZ(0)",
  },
  heroBgImage: {
    position: "absolute",
    top: "-12%",
    left: 0,
    width: "100%",
    height: "124%",
    objectFit: "cover",
    objectPosition: "center",
    willChange: "transform",
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
  },
  heroUi: {
    position: "absolute",
    inset: 0,
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    boxSizing: "border-box",
    padding: theme.spacing(2),
    paddingTop: 72,
    transform: "translateZ(0)",
    backfaceVisibility: "hidden",
    pointerEvents: "none",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.5),
      paddingTop: 64,
    },
  },
  racePaceHeader: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    minHeight: 56,
    padding: theme.spacing(1.5, 2),
    boxSizing: "border-box",
    backgroundColor: "transparent",
    transition: "background-color 0.25s ease, box-shadow 0.25s ease",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1, 1.5),
      minHeight: 52,
    },
  },
  racePaceHeaderSolid: {
    backgroundColor: "#2d2d2d",
    boxShadow: "0 1px 0 rgba(0,0,0,0.2)",
  },
  heroUiCenter: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 0,
    width: "100%",
    pointerEvents: "auto",
  },
  heroUiBottom: {
    flexShrink: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    paddingBottom: theme.spacing(1),
    pointerEvents: "auto",
  },
  heroScrollButton: {
    color: "#fff",
    filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.4))",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.12)",
    },
  },
  heroLogoRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    maxWidth: "100%",
    [theme.breakpoints.down("xs")]: {
      gap: theme.spacing(1),
    },
  },
  heroLogoMark: {
    height: 40,
    width: "auto",
    flexShrink: 0,
    display: "block",
    objectFit: "contain",
    objectPosition: "left center",
    [theme.breakpoints.down("xs")]: {
      height: 32,
    },
  },
  heroLogoText: {
    color: "#fff",
    fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
    fontSize: "1.125rem",
    fontWeight: 600,
    letterSpacing: "0.02em",
    lineHeight: 1.2,
    textShadow: "0 1px 8px rgba(0,0,0,0.35)",
    whiteSpace: "nowrap",
    [theme.breakpoints.down("xs")]: {
      fontSize: "1rem",
    },
  },
  whatIsSection: {
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    backgroundColor: "#000",
    color: "#fff",
  },
  /** Offset fixed Race Pace header when using scrollIntoView from the hero CTA. */
  postHeroScrollTarget: {
    scrollMarginTop: 72,
    [theme.breakpoints.down("xs")]: {
      scrollMarginTop: 56,
    },
  },
  whatIsTitle: {
    fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
    fontWeight: 700,
    fontStyle: "italic",
    letterSpacing: "0.02em",
    color: "#fff",
  },
  whatIsBody: {
    color: "rgba(255,255,255,0.95)",
    fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
    fontSize: "1rem",
    lineHeight: 1.65,
    fontWeight: 400,
    marginTop: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.9375rem",
    },
  },
  whatIsImageBleed: {
    marginTop: theme.spacing(4),
    lineHeight: 0,
    fontSize: 0,
    overflow: "hidden",
  },
  whatIsImage: {
    width: "100%",
    height: 250,
    objectFit: "cover",
    objectPosition: "center",
    display: "block",
    verticalAlign: "top",
  },
  racePaceIconsSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: "#000",
    color: "#fff",
    paddingTop: 50,
    paddingBottom: 50,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  racePaceIconRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "flex-start",
    width: "100%",
    maxWidth: 1040,
    marginLeft: "auto",
    marginRight: "auto",
    gap: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "center",
      gap: theme.spacing(4),
    },
  },
  racePaceIconItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    flex: "1 1 0",
    minWidth: 0,
    maxWidth: 340,
    [theme.breakpoints.down("xs")]: {
      flex: "none",
      maxWidth: 360,
    },
  },
  racePaceIconImg: {
    height: 104,
    width: "auto",
    maxWidth: "100%",
    objectFit: "contain",
    objectPosition: "center bottom",
    display: "block",
    marginBottom: theme.spacing(2.5),
  },
  racePaceIconLabel: {
    margin: 0,
    color: "#fff",
    fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
    fontSize: "1.0625rem",
    lineHeight: 1.5,
    fontWeight: 400,
    maxWidth: 300,
    [theme.breakpoints.down("xs")]: {
      maxWidth: 340,
    },
  },
  whatIsTextContainer: {
    paddingTop: 50,
    paddingBottom: 50,
    boxSizing: "border-box",
  },
  heroContent: {
    textAlign: "center",
    padding: theme.spacing(2, 3),
    maxWidth: 720,
    marginLeft: "auto",
    marginRight: "auto",
    width: "100%",
    minWidth: 0,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    willChange: "transform",
  },
  heroHeadline: {
    color: "#fff",
    fontWeight: 700,
    fontStyle: "italic",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
    fontSize: "61.3px",
    lineHeight: 1.08,
    margin: 0,
    marginBottom: theme.spacing(2),
    textShadow: "0 2px 24px rgba(0,0,0,0.45)",
    maxWidth: "100%",
    [theme.breakpoints.down("sm")]: {
      fontSize: "clamp(1.75rem, 7vw, 3rem)",
      letterSpacing: "0.02em",
    },
  },
  heroSubtitle: {
    color: "rgba(255,255,255,0.96)",
    fontSize: "18.66px",
    fontWeight: 400,
    lineHeight: 1.5,
    margin: 0,
    textShadow: "0 1px 16px rgba(0,0,0,0.5)",
  },
  heroSectionNoPad: {
    paddingTop: 0,
    paddingBottom: 0,
    marginTop: -24,
    [theme.breakpoints.down("sm")]: {
      marginTop: -16,
    },
  },
  meetDriversSection: {
    padding: "50px 0",
    margin: 0,
    display: "block",
    position: "relative",
    boxSizing: "border-box",
  },
  meetDriversRoot: {
    position: "relative",
    minHeight: 320,
    display: "flex",
    alignItems: "center",
    isolation: "isolate",
    [theme.breakpoints.down("xs")]: {
      minHeight: 280,
    },
  },
  meetDriversBg: {
    position: "absolute",
    inset: 0,
    zIndex: 0,
    backgroundImage: `url(${RACEPACE_HERO_URL})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  },
  meetDriversOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 1,
    pointerEvents: "none",
    background:
      "linear-gradient(90deg, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.55) 38%, rgba(0,0,0,0.12) 62%, transparent 85%)",
  },
  meetDriversContent: {
    position: "relative",
    zIndex: 2,
    boxSizing: "border-box",
    width: "100%",
    maxWidth: 520,
    minWidth: 0,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    padding: theme.spacing(6, 3, 6, 0),
    paddingLeft: "clamp(24px, 18vw, 200px)",
    paddingRight: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(4),
      paddingLeft: theme.spacing(2.5),
      maxWidth: "100%",
    },
  },
  meetDriversTitle: {
    color: "#fff",
    textAlign: "left",
    fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
    fontWeight: 700,
    fontStyle: "italic",
    letterSpacing: "0.02em",
    lineHeight: 1.2,
    margin: 0,
    marginBottom: theme.spacing(2),
  },
  meetDriversBody: {
    color: "rgba(255,255,255,0.96)",
    textAlign: "left",
    fontFamily: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
    fontSize: "1rem",
    fontWeight: 400,
    fontStyle: "normal",
    lineHeight: 1.65,
    margin: 0,
    whiteSpace: "pre-wrap",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.9375rem",
    },
  },
  driversShowcaseSection: {
    backgroundColor: "#000",
    color: "#fff",
    padding: 0,
    margin: 0,
    width: "100%",
    boxSizing: "border-box",
  },
  driversPhotoStrip: {
    lineHeight: 0,
    fontSize: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxSizing: "border-box",
    overflow: "hidden",
    minWidth: 0,
  },
  driversPhotoRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    width: "100%",
    maxWidth: "min(1154px, 100%)",
    minWidth: 0,
    margin: 0,
    padding: 0,
  },
  driversPhotoHalf: {
    flex: "1 1 50%",
    minWidth: 0,
    width: "auto",
    height: "600px",
    objectFit: "cover",
    objectPosition: "center",
    display: "block",
    [theme.breakpoints.down("xs")]: {
      height: 200,
    },
  },
  driversIconsBlock: {
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(15),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  driversPillarIconImg: {
    height: 112,
    width: "auto",
    maxWidth: "100%",
    objectFit: "contain",
    objectPosition: "center bottom",
    display: "block",
    marginBottom: theme.spacing(2.5),
    [theme.breakpoints.down("xs")]: {
      height: 96,
    },
  },
  driversDivider: {
    margin: "50px 0",
    backgroundColor: "#fff",
    height: "1px",
    border: "none",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
}));

const HERO_PARALLAX_IMG = 0.16;
const HERO_PARALLAX_TEXT = 0.38;
/** Fixed header picks up solid gray background after this many pixels of vertical scroll. */
const RACEPACE_HEADER_SOLID_AFTER_SCROLL_PX = 200;

/** Nearest ancestor that actually scrolls (layout uses overflow-y on a panel instead of the window). */
function findNearestScrollContainer(startEl) {
  let el = startEl;
  while (el && el !== document.body) {
    const { overflowY } = window.getComputedStyle(el);
    const oy =
      overflowY === "auto" ||
      overflowY === "scroll" ||
      overflowY === "overlay";
    if (oy && el.scrollHeight > el.clientHeight + 1) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

function getVerticalScrollPixels(scrollContainer) {
  if (scrollContainer) {
    return scrollContainer.scrollTop;
  }
  return (
    window.pageYOffset ||
    window.scrollY ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0
  );
}

function RacePace() {
  const classes = useStyles();
  const { t } = useTranslation("starfish");
  const heroRef = useRef(null);
  const postHeroSectionRef = useRef(null);
  const racePacePageRootRef = useRef(null);
  const [heroParallax, setHeroParallax] = useState({ img: 0, text: 0 });
  const [viewportClientWidth, setViewportClientWidth] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.clientWidth
      : null,
  );
  const [headerScrolled, setHeaderScrolled] = useState(false);

  useEffect(() => {
    const update = () => {
      setViewportClientWidth(document.documentElement.clientWidth);
    };
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  /** Prevent full-bleed / subpixel layout from causing document horizontal scroll on this page. */
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflowX;
    const prevBody = body.style.overflowX;
    html.style.overflowX = "hidden";
    body.style.overflowX = "hidden";
    return () => {
      html.style.overflowX = prevHtml;
      body.style.overflowX = prevBody;
    };
  }, []);

  useLayoutEffect(() => {
    const root = racePacePageRootRef.current;
    const nested = root ? findNearestScrollContainer(root) : null;

    const updateHeaderFromScroll = () => {
      const y = getVerticalScrollPixels(nested);
      setHeaderScrolled(y >= RACEPACE_HEADER_SOLID_AFTER_SCROLL_PX);
    };

    updateHeaderFromScroll();

    window.addEventListener("scroll", updateHeaderFromScroll, {
      passive: true,
      capture: true,
    });
    document.addEventListener("scroll", updateHeaderFromScroll, {
      passive: true,
      capture: true,
    });
    if (nested) {
      nested.addEventListener("scroll", updateHeaderFromScroll, {
        passive: true,
      });
    }

    return () => {
      window.removeEventListener("scroll", updateHeaderFromScroll, {
        capture: true,
      });
      document.removeEventListener("scroll", updateHeaderFromScroll, {
        capture: true,
      });
      if (nested) {
        nested.removeEventListener("scroll", updateHeaderFromScroll);
      }
    };
  }, []);

  useEffect(() => {
    const mq =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq && mq.matches) {
      return undefined;
    }

    let rafId = 0;
    const update = () => {
      rafId = 0;
      const el = heroRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      if (rect.bottom < 0 || rect.top > vh) {
        setHeroParallax({ img: 0, text: 0 });
        return;
      }
      const t = -rect.top;
      setHeroParallax({
        img: t * HERO_PARALLAX_IMG,
        text: t * HERO_PARALLAX_TEXT,
      });
    };
    const onScrollOrResize = () => {
      if (!rafId) rafId = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    update();
    return () => {
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  const scrollDownOneViewport = () => {
    if (typeof window === "undefined") return;
    const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    const behavior = smooth ? "smooth" : "auto";

    const target = postHeroSectionRef.current;
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ behavior, block: "start" });
      return;
    }

    const root = document.scrollingElement ?? document.documentElement;
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const maxScroll = Math.max(0, root.scrollHeight - root.clientHeight);
    const next = Math.min(root.scrollTop + vh, maxScroll);
    root.scrollTop = next;
  };

  const fullBleedStyle = (() => {
    if (viewportClientWidth == null) return undefined;
    const w = Math.max(0, Math.floor(viewportClientWidth));
    const half = Math.floor(w / 2);
    return {
      width: w,
      maxWidth: w,
      marginLeft: `calc(50% - ${half}px)`,
      marginRight: `calc(50% - ${half}px)`,
    };
  })();

  return (
    <div ref={racePacePageRootRef} className={classes.racePaceContainer}>
      <Box
        component="header"
        className={clsx(
          classes.racePaceHeader,
          headerScrolled && classes.racePaceHeaderSolid,
        )}
        aria-label={t("racepace.hero_logo_alt")}
      >
        <Box className={classes.heroLogoRow}>
          <img
            src={RACEPACE_LOGO_URL}
            alt=""
            className={classes.heroLogoMark}
          />
          <Typography
            component="span"
            variant="inherit"
            className={classes.heroLogoText}
          >
            {t("racepace.race_pace")}
          </Typography>
        </Box>
      </Box>

      <section className={clsx(classes.section, classes.heroSectionNoPad)}>
        <div className={classes.heroBleed} style={fullBleedStyle}>
          <div className={classes.hero} ref={heroRef}>
            <div className={classes.heroMedia}>
              <img
                src={RACEPACE_HERO_URL}
                alt=""
                className={classes.heroBgImage}
                decoding="async"
                style={{
                  transform: `translate3d(0, ${heroParallax.img}px, 0)`,
                }}
              />
              <div className={classes.heroOverlay} aria-hidden />
            </div>
            <div className={classes.heroUi}>
              <div className={classes.heroUiCenter}>
                <div
                  className={classes.heroContent}
                  style={{
                    transform: `translate3d(0, ${heroParallax.text}px, 0)`,
                  }}
                >
                  <Typography
                    component="h1"
                    variant="inherit"
                    className={classes.heroHeadline}
                  >
                    {t("racepace.hero_headline")}
                  </Typography>
                  <Typography
                    component="p"
                    variant="inherit"
                    className={classes.heroSubtitle}
                  >
                    {t("racepace.subtitle")}
                  </Typography>
                </div>
              </div>
              <div className={classes.heroUiBottom}>
                <IconButton
                  type="button"
                  className={classes.heroScrollButton}
                  aria-label={t("racepace.scroll_down")}
                  onClick={scrollDownOneViewport}
                  size="medium"
                >
                  <KeyboardArrowDownIcon fontSize="large" />
                </IconButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        ref={postHeroSectionRef}
        className={clsx(
          classes.section,
          classes.whatIsSection,
          classes.postHeroScrollTarget,
        )}
      >
        <div
          className={clsx(classes.textContainer, classes.whatIsTextContainer)}
        >
          <Typography
            align="center"
            className={clsx(classes.headText, classes.whatIsTitle)}
            variant="h3"
          >
            {t("racepace.what_is")}
          </Typography>
          <Typography
            align="center"
            component="div"
            className={clsx(classes.whatIsBody, classes.preWrapText)}
            variant="body1"
          >
            {t("racepace.what_is_description")}
          </Typography>
        </div>
        <div className={classes.whatIsImageBleed} style={fullBleedStyle}>
          <img
            src={RACEPACE_WHAT_IMAGE_URL}
            alt=""
            className={classes.whatIsImage}
            loading="lazy"
            decoding="async"
          />
        </div>
      </section>

      <section className={classes.racePaceIconsSection}>
        <div className={classes.racePaceIconRow}>
          <div className={classes.racePaceIconItem}>
            <img
              src={RACEPACE_ICON_3_URL}
              alt=""
              className={classes.racePaceIconImg}
              decoding="async"
            />
            <Typography
              component="p"
              align="center"
              className={classes.racePaceIconLabel}
            >
              {t("racepace.circle1")}
            </Typography>
          </div>
          <div className={classes.racePaceIconItem}>
            <img
              src={RACEPACE_ICON_LIST_URL}
              alt=""
              className={classes.racePaceIconImg}
              decoding="async"
            />
            <Typography
              component="p"
              align="center"
              className={classes.racePaceIconLabel}
            >
              {t("racepace.circle2")}
            </Typography>
          </div>
          <div className={classes.racePaceIconItem}>
            <img
              src={RACEPACE_ICON_CALENDAR_URL}
              alt=""
              className={classes.racePaceIconImg}
              decoding="async"
            />
            <Typography
              component="p"
              align="center"
              className={classes.racePaceIconLabel}
            >
              {t("racepace.circle3")}
            </Typography>
          </div>
        </div>
      </section>

      <section className={classes.meetDriversSection} style={fullBleedStyle}>
        <div className={classes.meetDriversRoot}>
          <div className={classes.meetDriversBg} aria-hidden />
          <div className={classes.meetDriversOverlay} aria-hidden />
          <div className={classes.meetDriversContent}>
            <Typography
              component="h2"
              variant="h3"
              className={classes.meetDriversTitle}
            >
              {t("racepace.meet_the_drivers")}
            </Typography>
            <Typography
              component="div"
              variant="body1"
              className={classes.meetDriversBody}
            >
              {t("racepace.drivers_description")}
            </Typography>
          </div>
        </div>
      </section>

      <section className={classes.driversShowcaseSection}>
        <div className={classes.driversPhotoStrip} style={fullBleedStyle}>
          <div className={classes.driversPhotoRow}>
            <img
              src={RACEPACE_DRIVERS_PHOTO_1_URL}
              alt=""
              className={classes.driversPhotoHalf}
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
        <div className={classes.driversIconsBlock}>
          <div className={classes.racePaceIconRow}>
            <div className={classes.racePaceIconItem}>
              <img
                src={RACEPACE_ICON_COACH_URL}
                alt=""
                className={classes.driversPillarIconImg}
                loading="lazy"
                decoding="async"
              />
              <Typography
                component="p"
                align="center"
                className={classes.racePaceIconLabel}
              >
                {t("racepace.driver1")}
              </Typography>
            </div>
            <div className={classes.racePaceIconItem}>
              <img
                src={RACEPACE_ICON_ENCOURAGE_URL}
                alt=""
                className={classes.driversPillarIconImg}
                loading="lazy"
                decoding="async"
              />
              <Typography
                component="p"
                align="center"
                className={classes.racePaceIconLabel}
              >
                {t("racepace.driver2")}
              </Typography>
            </div>
            <div className={classes.racePaceIconItem}>
              <img
                src={RACEPACE_ICON_HELP_OTHERS_URL}
                alt=""
                className={classes.driversPillarIconImg}
                loading="lazy"
                decoding="async"
              />
              <Typography
                component="p"
                align="center"
                className={classes.racePaceIconLabel}
              >
                {t("racepace.driver3")}
              </Typography>
            </div>
          </div>
        </div>
        <div className={classes.driversPhotoStrip} style={fullBleedStyle}>
          <div className={classes.driversPhotoRow}>
            <img
              src={RACEPACE_DRIVERS_PHOTO_2_URL}
              alt=""
              className={classes.driversPhotoHalf}
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      <Divider className={classes.driversDivider} />
      <section className={clsx(classes.section)}>
        <div className={clsx(classes.textContainer)}>
          <Typography align="center" className={classes.headText} variant="h3">
            {t("racepace.closing_pre")}
            <span className={classes.linkColor}>
              {t("racepace.closing_highlight")}
            </span>
          </Typography>
          <Typography
            align="center"
            className={classes.mainText}
            variant="body1"
          >
            {t("racepace.start_today")}
          </Typography>
        </div>
      </section>
    </div>
  );
}

export default RacePace;
